// TESSERAX — NOUVEAUX SOMMETS : records personnels PAR DIMENSION (cœur pur, testé).
//
// Le jeu ne célébrait qu'UN seul record personnel : le SCORE (cf. records.ts /
// net.saveModeBest). Conséquence : une partie qui ne bat pas le score restait
// MUETTE même quand le joueur venait pourtant de réaliser sa plus longue chaîne,
// sa plus grande Profondeur ou sa première CROIX double — autant de progrès réels
// et ressentis, jamais reconnus. Or « j'ai fait mieux qu'avant » est le crochet de
// rétention le plus universel, et le multiplier sur PLUSIEURS axes donne au joueur
// un sommet à battre même les jours où le score ne suit pas (vecteurs de progrès
// multiples). On isole ici un registre de sommets personnels par DIMENSION.
//
// Module PUR : ni THREE ni DOM, pas de Math.random, pas d'I/O. main.ts gère la
// persistance localStorage ; ici on ne fait que parser / comparer / sérialiser.
// AFFICHAGE seul (aucun tirage altéré) ⇒ déterminisme du défi du jour intact.
//
// Le socle « registre numérique tolérant » (parse + meilleure valeur + sérialisation)
// est mutualisé dans core/numreg, partagé à l'identique avec core/records ; ici on ne
// garde que la sémantique propre aux sommets par dimension (catalogue + applyPeaks).
import { type NumRegistry, parseNumReg, serializeNumReg, bestIn } from './numreg';

// Catalogue DATA-DRIVEN des dimensions suivies — il croît par AJOUT d'entrées, pas
// par édition de flux de contrôle. `metric` est la clé stable (registre + i18n
// `peak.<metric>`), `icon` un glyphe géométrique (pas d'emoji, cf. DA console), et
// `labelFr` le repli autonome si l'i18n est absent. Le SCORE en est volontairement
// exclu : il garde sa propre célébration « RECORD » distincte (records.ts), et le
// dupliquer ici brouillerait le lexique.
export interface PeakDim {
  metric: string;
  icon: string;
  labelFr: string;
}
export const PEAK_DIMS: PeakDim[] = [
  { metric: 'combo',     icon: '◈', labelFr: 'Résonance' },
  // 'chain' = la CASCADE seule (bestChain), distincte de 'combo' (max(série, chaîne)) : les
  // succès combo4/7/10 testent s.chain — leur barre de progrès doit lire la MÊME grandeur,
  // sinon une série ×6 affichait une barre pleine sur un succès de chaîne encore verrouillé
  // (exactement le gradient malhonnête que MOT-ACH-PROGRESS-001 interdit).
  { metric: 'chain',     icon: '⛓', labelFr: 'Chaîne' },
  { metric: 'depth',     icon: '▼', labelFr: 'Profondeur' },
  { metric: 'cross',     icon: '✚', labelFr: 'Croix' },
  { metric: 'continuum', icon: '∞', labelFr: 'Continuum' },
  { metric: 'lines',     icon: '≡', labelFr: 'Lignes' },
  { metric: 'slabs',     icon: '▦', labelFr: 'Grilles' },
];

export type PeakRecords = NumRegistry;

// Parse tolérant d'un blob localStorage : JSON objet { metric: best } (cf. numreg).
export function parsePeaks(raw: string | null | undefined): PeakRecords {
  return parseNumReg(raw);
}

export function serializePeaks(p: PeakRecords): string {
  return serializeNumReg(p);
}

// Meilleure marque connue pour une dimension (0 si aucune).
export function peakFor(p: PeakRecords, metric: string): number {
  return bestIn(p, metric);
}

// Un sommet FRANCHI sur cette partie : la dimension, sa nouvelle valeur, l'ancienne
// marque (0 si première fois) et les métadonnées d'affichage (icône + repli FR).
export interface PeakHit {
  metric: string;
  value: number;
  prev: number;
  icon: string;
  labelFr: string;
}

// Confronte les valeurs de la run au registre. PUR : ne mute pas `prev`, renvoie un
// registre NEUF si au moins un sommet est battu (sinon `prev` tel quel). Un sommet
// n'est « battu » qu'à partir d'une valeur > 0 ET strictement supérieure à l'ancienne
// marque (égaler ne célèbre pas — comme records.applyRecord). On itère le CATALOGUE
// (pas `run`) → l'ordre des sommets battus est stable et déterministe, et les clés
// inconnues de la run sont ignorées. Tolérant aux valeurs non finies / négatives.
export interface PeaksOutcome { peaks: PeakRecords; beaten: PeakHit[]; }
export function applyPeaks(prev: PeakRecords, run: Record<string, number>): PeaksOutcome {
  const beaten: PeakHit[] = [];
  let next: PeakRecords | null = null;
  for (const dim of PEAK_DIMS) {
    const raw = run[dim.metric];
    const v = Number.isFinite(raw) ? Math.floor(raw) : 0;
    if (v <= 0) continue;
    const old = peakFor(prev, dim.metric);
    if (v > old) {
      if (!next) next = { ...prev };
      next[dim.metric] = v;
      beaten.push({ metric: dim.metric, value: v, prev: old, icon: dim.icon, labelFr: dim.labelFr });
    }
  }
  return { peaks: next || prev, beaten };
}
